import React from 'react'

// Components
import Tooltip from 'components/Tooltip'
import { RoleDropdown } from '../../../RoleDropdown'

// Types
import { Member } from '../../../../types'

// Styles
import { Wrapper, StyledMemberCard } from './Item.styles'

interface ItemProps {
  className?: string
  member: Member
  onEditor?: (member: Member) => void
  onViewer?: (member: Member) => void
  onRemove?: (member: Member) => void
  onToggleAdmin?: (member: Member) => void
  isDisabled?: boolean
  hasSingleEditor?: boolean
}

/**
 * Item
 *
 * Renders a single item for the MembersList parent component
 *
 * @example
 * <Item
 *  member={...}
 *  onEditor={() => {...}}
 *  onViewer={() => {...}}
 *  onRemove={(member) => {...}}
 * />
 */
export const Item: React.FC<ItemProps> = ({
  className,
  member,
  onRemove,
  onEditor,
  onViewer,
  onToggleAdmin,
  isDisabled,
  hasSingleEditor,
}) => {
  const isRemovable = !!onRemove && !member.isOwner
  const showSingleEditorMessage = hasSingleEditor && member.isEditor

  return (
    <Wrapper className={className} data-testid="member-list-item">
      <StyledMemberCard
        name={member.name}
        avatarSrc={member.avatar}
        email={member.email}
      />
      <Tooltip
        placement="bottom-end"
        content="You need to have at least one Editor on your workspace"
        disabled={!showSingleEditorMessage}>
        <RoleDropdown
          onEditor={() => onEditor?.(member)}
          onViewer={() => onViewer?.(member)}
          onRemove={() => onRemove?.(member)}
          onToggleAdmin={() => onToggleAdmin?.(member)}
          isEditor={member.isEditor}
          isAdmin={member.isAdmin}
          isOwner={member.isOwner}
          isDisabled={isDisabled || showSingleEditorMessage}
          isRemovable={isRemovable}
          showAdminToggle={!member.isOwner}
          showBadges
        />
      </Tooltip>
    </Wrapper>
  )
}
